import { css } from '@emotion/react';

import type { FollowDebugData } from '@/hooks/use-follow-debug';
import { darkTheme } from '@/theme/dark-theme';
import { fontFamilies } from '@/theme/tokens';

export interface DebugConsoleProps {
  data: FollowDebugData;
  onClose: () => void;
}

function timeSince(ms: number | null): string {
  if (ms === null) return 'never';
  const ago = Math.round((Date.now() - ms) / 1000);
  return `${ago}s ago`;
}

export function DebugConsole({ data, onClose }: DebugConsoleProps) {
  const recentTransport = data.transportLog.slice(-5).reverse();

  return (
    <div
      data-testid="debug-console"
      css={css`
        flex-shrink: 0;
        background: rgba(31, 22, 51, 0.94);
        backdrop-filter: blur(4px);
        border-bottom: 1px solid ${darkTheme.border};
        z-index: 20;
        font-family: ${fontFamilies.mono};
        font-size: 11px;
        line-height: 1.5;
        color: ${darkTheme.text};
        padding: 6px 10px;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          padding-bottom: 4px;
          border-bottom: 1px solid ${darkTheme.border};
        `}
      >
        <span
          css={css`
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: ${darkTheme.textSecondary};
          `}
        >
          Debug Console
        </span>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          <button
            type="button"
            onClick={data.downloadLog}
            aria-label="Download debug log"
            css={css`
              background: none;
              border: none;
              color: ${darkTheme.textSecondary};
              cursor: pointer;
              font-size: 12px;
              padding: 0 2px;
              line-height: 1;

              &:hover {
                color: ${darkTheme.text};
              }
            `}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close debug console"
            css={css`
              background: none;
              border: none;
              color: ${darkTheme.textSecondary};
              cursor: pointer;
              font-size: 14px;
              padding: 0 2px;
              line-height: 1;

              &:hover {
                color: ${darkTheme.text};
              }
            `}
          >
            &times;
          </button>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: 0 12px;
          max-height: 140px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: ${darkTheme.border} transparent;
        `}
      >
        {/* Sync */}
        <Section title="Sync">
          <Row label="Drift">{data.driftMs}ms</Row>
          <Row label="Connection">{data.connectionState}</Row>
          <Row label="Driver">{data.driverStatus}</Row>
          <Row label="Clock offset">{Math.round(data.clockOffset)}ms</Row>
          <Row label="RTT">{Math.round(data.clockRtt)}ms</Row>
        </Section>

        {/* Playback */}
        <Section title="Playback">
          <Row label="Section">
            {data.currentSectionIndex + 1} / {data.sectionCount}
          </Row>
          <Row label="Rate">{data.playbackRate}x</Row>
          <Row label="Heartbeats">{data.heartbeatCount}</Row>
          <Row label="Last heartbeat">{timeSince(data.lastHeartbeatMs)}</Row>
        </Section>

        {/* Transport */}
        <Section title="Transport">
          {recentTransport.length === 0 ? (
            <div
              css={css`
                color: ${darkTheme.textSecondary};
              `}
            >
              No events yet
            </div>
          ) : (
            recentTransport.map((entry, i) => (
              <div
                key={i}
                css={css`
                  font-size: 10px;
                  margin-bottom: 1px;
                  display: flex;
                  gap: 6px;
                `}
              >
                <span
                  css={css`
                    color: ${darkTheme.textSecondary};
                    min-width: 50px;
                  `}
                >
                  {(entry.relativeMs / 1000).toFixed(1)}s
                </span>
                <span
                  css={css`
                    color: ${darkTheme.primary};
                    min-width: 60px;
                  `}
                >
                  {entry.command}
                </span>
                {entry.driftMs !== undefined && (
                  <span
                    css={css`
                      color: ${darkTheme.textSecondary};
                    `}
                  >
                    drift:{entry.driftMs}ms
                  </span>
                )}
              </div>
            ))
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      css={css`
        flex: 1;
        min-width: 180px;
        padding-right: 12px;
        border-right: 1px solid ${darkTheme.border}33;

        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      `}
    >
      <div
        css={css`
          font-weight: 600;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${darkTheme.primary};
          margin-bottom: 3px;
        `}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1px;
      `}
    >
      <span
        css={css`
          color: ${darkTheme.textSecondary};
        `}
      >
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}
